import { Component, ChangeDetectionStrategy, input, output, signal, ElementRef, viewChild } from '@angular/core';
import { Upload } from '@xmedical/core';

export type DownloadRequestType = 'upload' | 'generated-report';

export interface DownloadRequest {
  upload: Upload;
  type: DownloadRequestType;
}

@Component({
  selector: 'xmed-file-downloader',
  exportAs: 'libFileDownloader',
  standalone: true,
  imports: [],
  templateUrl: './file-downloader.component.html',
  styleUrl: './file-downloader.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileDownloaderComponent {
  private readonly fileLink = viewChild<ElementRef<HTMLAnchorElement>>('fileLink');

  upload = input.required<Upload>();

  private readonly _processing = signal(false);
  processing = this._processing.asReadonly();
  downloadRequested = output<DownloadRequest>();

  fileUrl = signal('');

  /**
   * Called by parent/template to request a file download.
   * Emits downloadRequested so the parent can resolve the signed URL
   * and call completeDownload() with it.
   */
  openUpload(): void {
    this._processing.set(true);
    this.downloadRequested.emit({ upload: this.upload(), type: 'upload' });
  }

  /**
   * Called by parent/template to request a generated report download.
   * Emits downloadRequested so the parent can resolve the signed URL
   * and call completeDownload() with it.
   */
  openGeneratedReport(): void {
    this._processing.set(true);
    this.downloadRequested.emit({ upload: this.upload(), type: 'generated-report' });
  }

  /**
   * Called by the parent after resolving the signed URL.
   * Opens the file in a new tab via the hidden anchor element.
   */
  completeDownload(url: string): void {
    this.fileUrl.set(url);
    setTimeout(() => {
      this._processing.set(false);
      this.fileLink()?.nativeElement.click();
    }, 1);
  }

  /**
   * Called by the parent if URL resolution fails.
   */
  cancelDownload(): void {
    this._processing.set(false);
  }
}
